import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import CurrencyToggle from "@/components/CurrencyToggle";
import { LogOut, LayoutGrid, Users, Settings, FileText, Gauge } from "lucide-react";

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const isAdmin = user?.role === "admin";

  const navItems = isAdmin
    ? [
        { to: "/admin", label: "Overview", icon: Gauge },
        { to: "/admin/partners", label: "Partners", icon: Users },
        { to: "/admin/distributions", label: "Distributions", icon: FileText },
        { to: "/admin/config", label: "Configuration", icon: Settings },
      ]
    : [{ to: "/dashboard", label: "My Dashboard", icon: LayoutGrid }];

  return (
    <div className="min-h-screen bg-atf-bg flex flex-col">
      <header className="border-b border-atf-border bg-white">
        <div className="max-w-[1400px] mx-auto flex items-center justify-between px-8 py-5">
          <Link to={isAdmin ? "/admin" : "/dashboard"} className="flex items-baseline gap-3" data-testid="brand-link">
            <span className="font-serif text-3xl tracking-tight text-atf-brand">AT&amp;F</span>
            <span className="eyebrow hidden sm:inline">Partnership Ledger</span>
          </Link>
          <div className="flex items-center gap-6">
            <CurrencyToggle />
            <div className="hidden md:flex items-center gap-3">
              {user?.picture ? (
                <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-atf-subtle border border-atf-border" />
              )}
              <div className="text-right">
                <div className="text-sm font-medium text-atf-text leading-tight" data-testid="header-user-name">{user?.name}</div>
                <div className="eyebrow !text-[10px]" data-testid="header-user-role">{user?.role}</div>
              </div>
            </div>
            <button
              onClick={logout}
              data-testid="logout-button"
              className="border border-atf-border px-3 py-1.5 text-xs uppercase tracking-[0.18em] font-semibold text-atf-text2 hover:bg-atf-subtle transition-colors flex items-center gap-2"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
        <nav className="border-t border-atf-border bg-atf-bg">
          <div className="max-w-[1400px] mx-auto px-8 flex gap-1 overflow-x-auto">
            {navItems.map((item) => {
              const active = loc.pathname === item.to || (item.to !== "/admin" && loc.pathname.startsWith(item.to));
              const Icon = item.icon;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`px-4 py-3 text-xs uppercase tracking-[0.18em] font-semibold border-b-2 transition-colors flex items-center gap-2
                    ${active ? "border-atf-brand text-atf-brand" : "border-transparent text-atf-text2 hover:text-atf-text"}`}
                >
                  <Icon size={14} /> {item.label}
                </Link>
              );
            })}
          </div>
        </nav>
      </header>
      <main className="flex-1 max-w-[1400px] w-full mx-auto px-8 py-10">{children}</main>
      <footer className="border-t border-atf-border bg-white">
        <div className="max-w-[1400px] mx-auto px-8 py-6 flex flex-wrap items-center justify-between gap-4">
          <div className="eyebrow">AT&amp;F International, Inc. · Global Partnership</div>
          <div className="text-xs text-atf-text2">A merit-based, point-driven distribution model.</div>
        </div>
      </footer>
    </div>
  );
}
